// CashTable.tsx
import {Popover, Table, TableColumnsType} from 'antd';
import dayjs from 'dayjs';
import { Tag, Space } from 'antd';
import React, {useState} from 'react';
import { CashTx } from '../../../types/CashTx';
import { formatKorDate } from '../../../util/date';
import ColumnSubText from '../../common/table/cell/ColumnSubText';
import {formatCurrency, formatPhoneNumber, getSubscriptionStatusStr} from '../../../util/format';
import AccountNumberRender from './AccountNumberRender';
import userAPI from "../../../api/userAPI";
import styles from "../../../containers/BaechaList/BaechaListTable/BaechaListTable.module.scss";

interface Props {
  showCashRequestPopup: (record: CashTx) => void;
	dataSource: CashTx[];
}

const CashTable = ({ showCashRequestPopup, dataSource }: Props) => {
	const [openPopover, setOpenPopover] = useState<string | undefined>(undefined);
	const [popupData, setPopupData] = useState<any>(undefined);

	const onOpenPopup = async (bool: boolean, cashTxId: string, userId?: string) => {
		if (bool && userId) {
			const res = await userAPI.fetchUserDataAPI(userId);
			setPopupData(res);
			setOpenPopover(cashTxId);
		} else {
			setPopupData(undefined);
			setOpenPopover(undefined);
		}
	};

	const onClosePopup = (e: any) => {
		e.preventDefault();
		setOpenPopover(undefined);
		setPopupData(undefined);
	};

	const columns: TableColumnsType<CashTx> = [
		{
			title: '상태',
			dataIndex: 'status',
			align: 'center',
			width: 80,
			render: (text: string) =>
				(<div style={{ textAlign: 'center' }}>
					{(text === 'hold')
						? <Tag color="orange" key={text} style={{ margin: 0 }}>대기</Tag>
						: <Tag color="green" key={text} style={{ margin: 0 }}>완료</Tag>}
				</div>)
		},
		{
			title: '요청일',
			dataIndex: 'createDate',
			width: 130,
			render: (text: string) => (
				<div>
					<div>{formatKorDate(text)}</div>
					<ColumnSubText text={dayjs(text).format('HH:mm')}/>
				</div>
			)
		},
		{
			title: '요청자',
			width: 130,
			render: (value: any, record: CashTx) => {
				const requestUser = record.requestType === 'deposit' ? record.toUserName : record.fromUserName;
				const requestUserPhone = record.requestType === 'deposit' ? record.toUserPhone : record.fromUserPhone;

				return (
					<Popover
						className={styles.clickable}
						placement="top"
						content={(
							<a onClick={onClosePopup}>
								{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').length > 0 && (
									<div>
										{popupData?.memberInfo?.filter((item: any) => item.status === 'ok')
											.map((item: any) => <div key={item.name}>{item.name}</div>)}
									</div>
								)}
							</a>
						)}
						trigger="click"
						title={(
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								<div>{requestUser}</div>
								<ColumnSubText text={formatPhoneNumber(requestUserPhone)} />
							</div>
						)}
						open={openPopover === record.id}
						onOpenChange={(bool) => {
							onOpenPopup(bool, record.id, record.requestType === 'deposit' ? record.toUserId : record.fromUserId)
						}}
					>
						<div>
							<div>{requestUser}</div>
							<ColumnSubText text={formatPhoneNumber(requestUserPhone)} />
						</div>
					</Popover>
				)
			},
		},
		{
			title: '입금자명',
			dataIndex: 'accountName',
			width: 130,
			render: (text: string) => text,
		},
		{
			title: '구독상태',
			dataIndex: 'subscriptionStatus',
			width: 80,
			align: 'center',
			render: (text: string, record: any) => {
				const str = getSubscriptionStatusStr(text);
				switch (text) {
					case 'ACTIVE':
					case 'CANCELED':
					case 'IN_GRACE':
						return <Tag color="blue" key={record.subscriptionId} style={{ margin: 0 }}>{str}</Tag>;
					case 'EXPIRED':
						return <Tag color="grey" key={record.subscriptionId} style={{ margin: 0 }}>{str}</Tag>;
					case 'REVOKED':
					case 'REFUNDED':
						return <Tag color="red" key={record.subscriptionId} style={{ margin: 0 }}>{str}</Tag>;
					default:
						return text;
				}
			},
		},
		{
			title: '구분',
			dataIndex: 'requestType',
			align: 'center',
			width: 80,
			render: (text: string) =>
				<div style={{ textAlign: 'center' }}>
					{(text === 'deposit')
						? <Tag color="blue" key={text} style={{ margin: 0 }}>충전</Tag>
						: <Tag color="red" key={text} style={{ margin: 0 }}>출금</Tag>}
				</div>
		},
		{
			title: '금액',
			dataIndex: 'amount',
			align: 'right',
			width: 150,
			render: (text: string) => (<span>{formatCurrency(Number(text), 'KRW')}</span>)
		},
		{
			title: '사유',
			dataIndex: 'reason',
			render: (text: string, record) => {
				if (record.reasonCode) {
					return (
						<>
							<Tag color="#BF0918" key={text}>이벤트</Tag>
							{text}
						</>
					);
				}
				return text;
			}
		},
		{
			title: '계좌정보',
			dataIndex: 'accountNumber',
			render:
				(text: string, record) => (
					<>
						{ text ? (
							<AccountNumberRender text={text} record={{
								accountName: record.accountName as string,
								accountBank: record.accountBank as string,
							} } />
						) : <div></div>}
					</>
				)
		},
		{
			title: '경과시간',
			width: 80,
			align: 'center',
			dataIndex: 'createDate',
			render: (text) => {
				return (<div>{dayjs(text).fromNow()}</div>)
			}
		},
		{
			title: '확인',
			align: 'center',
			fixed: 'right',
			width: 130,
			onCell: (record) => ({
				style: { backgroundColor: record.status === 'hold' ? '#D5E6FD': '' }
			}),
			render: (_, record) => {
				const isHold = record.status === 'hold';
				const isDone = record.status === 'done';
				const isCancel = record.status === 'cancel';
				const isFromUserOrToUserUpdate = record.updateUser === record.fromUserId || record.updateUser === record.toUserId;

				const getDotColor = () => {
					if (isCancel && isFromUserOrToUserUpdate) return 'orange';
					if (isCancel && !isFromUserOrToUserUpdate) return 'red';
					if (isDone) return 'green';
					return '';
				}

				const getText = () => {
					if (isCancel && isFromUserOrToUserUpdate) return '취소';
					if (isCancel && !isFromUserOrToUserUpdate) return '거절';
					if (isDone) return '승인';
					return '';
				};

				return (
					<Space size="middle">
						<a
							style={{ color: isHold ? '#155DFE' : 'black' }}
							onClick={(e) => {
								e.preventDefault();
								showCashRequestPopup(record);
							}}
						>
							{isHold
								? <>미확인</>
								: <span><span style={{ color: getDotColor(), marginRight: '4px'}}>●</span>{getText()}</span>
							}
						</a>
					</Space>
				);
			},
		},
	];

	return (
		<Table
			bordered
			columns={columns}
			dataSource={dataSource}
			rowKey="id"
			size="small"
			scroll={{
				x: 'max-content',
			}}
			pagination={false}
		/>
	)
}

export default CashTable;
